/**
 * Study Anything: read one of your uploaded books with the AI tutor beside it.
 *
 * The reading page (components/ContentViewer/HTMLContentViewer) for a Markdown
 * book: the book rendered from Markdown, its headings as a table of contents,
 * and the tutor panel on the right (../Books/ReaderTutorPanel) with the book
 * tutor rewired to this book (./StudyTutor). Selecting a passage puts it in
 * the composer; an answer's source scrolls the book to the heading it names.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Button, CircularProgress, Collapse, Drawer, IconButton, List, ListItem, ListItemText, Typography,
  useMediaQuery, useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";

import ReaderTutorPanel from "../Books/ReaderTutorPanel";
import { TOC_WIDTH, readTocOpen, saveTocOpen } from "../Books/readerToc";
import StudyTutor from "./StudyTutor";
import { errorMessage, getBook, getBookContent } from "./studyApi";
import { headingsIn, placeIn, revealSource, selectionIn } from "./markdownReader";

const FONT = "Be Vietnam Pro";
const PLUGINS = [remarkGfm, remarkMath];
const REHYPE = [rehypeKatex];

/** The headings grouped under their nearest level-1 heading, for the table of contents. */
export function groupHeadings(headings) {
  const groups = [];
  let current = null;
  headings.forEach((h) => {
    if (h.level === 1 || !current) {
      current = { ...h, children: [] };
      groups.push(current);
    } else if (h.level <= 3) {
      current.children.push(h);
    }
  });
  return groups;
}

export default function StudyReader() {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const contentRef = useRef(null);
  const paneRef = useRef(null);

  const [book, setBook] = useState(null);
  const [markdown, setMarkdown] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toc, setToc] = useState([]);
  // The contents bar: a sheet on phones; on wide screens collapsible, and remembered.
  const [drawerOpen, setDrawerOpen] = useState(() => (isMobile ? false : readTocOpen()));
  const toggleToc = () => setDrawerOpen((cur) => { if (!isMobile) saveTocOpen(!cur); return !cur; });
  const tocShown = drawerOpen && !isMobile;
  const [expanded, setExpanded] = useState({});
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    let on = true;
    setLoading(true);
    Promise.all([getBook(bookId), getBookContent(bookId)])
      .then(([row, text]) => { if (on) { setBook(row); setMarkdown(typeof text === "string" ? text : String(text ?? "")); } })
      .catch((e) => { if (on) setError(errorMessage(e, "Couldn't open this book")); })
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [bookId]);

  // The table of contents is read off the rendered book, so it matches what is on the page.
  useEffect(() => {
    if (!markdown || !contentRef.current) return;
    setToc(headingsIn(contentRef.current).map(({ id, text, level }) => ({ id, text, level })));
  }, [markdown]);

  const quoteText = useRef("");
  const onSelect = useCallback(() => {
    const picked = selectionIn(contentRef.current);
    if (picked && picked.text !== quoteText.current) {
      quoteText.current = picked.text;
      setQuote(picked);
    }
  }, []);
  const clearQuote = useCallback(() => { quoteText.current = ""; setQuote(null); }, []);
  useEffect(() => {
    let timer;
    const changed = () => { clearTimeout(timer); timer = setTimeout(onSelect, 600); };
    document.addEventListener("selectionchange", changed);
    return () => { clearTimeout(timer); document.removeEventListener("selectionchange", changed); };
  }, [onSelect]);
  const place = useCallback(() => placeIn(contentRef.current, paneRef.current), []);
  const onSource = useCallback((source) => {
    if (revealSource(contentRef.current, source) && isMobile) setDrawerOpen(false);
  }, [isMobile]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      if (isMobile) setDrawerOpen(false);
    }
  };
  const groups = useMemo(() => groupHeadings(toc), [toc]);
  const rendered = useMemo(() => (
    <ReactMarkdown remarkPlugins={PLUGINS} rehypePlugins={REHYPE}>{markdown}</ReactMarkdown>
  ), [markdown]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh"><CircularProgress /></Box>
    );
  }
  if (error || !book) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh"
        sx={{ backgroundColor: "#f5f5f5", p: 3 }}>
        <MenuBookIcon sx={{ fontSize: 80, color: "#bdbdbd", mb: 2 }} />
        <Typography variant="h5" sx={{ color: "#424242", mb: 1, fontWeight: 500, fontFamily: FONT }}>This book isn't here</Typography>
        <Typography sx={{ color: "#757575", mb: 3, textAlign: "center", maxWidth: 400, fontFamily: FONT }}>
          {error || "It may have been removed."}
        </Typography>
        <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={() => navigate("/dashboard/study")}
          sx={{ textTransform: "none", px: 3, py: 1 }}>Back to Study Anything</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100vh", backgroundColor: "#fff", display: "flex", overflow: "hidden", position: "fixed",
      top: 0, left: 0, right: 0, bottom: 0 }}>
      {(isMobile || drawerOpen) && (
      <Drawer variant={isMobile ? "temporary" : "permanent"} open={drawerOpen} onClose={() => setDrawerOpen(false)}
        sx={{ width: TOC_WIDTH, flexShrink: 0, "& .MuiDrawer-paper": { width: TOC_WIDTH, boxSizing: "border-box", top: "56px",
          height: "calc(100% - 56px)", borderRight: "1px solid #dadce0", borderTop: "1px solid #dadce0" } }}>
        <List aria-label="Contents" sx={{ py: 0, overflowY: "auto", height: "100%", "&::-webkit-scrollbar": { display: "none" },
          msOverflowStyle: "none", scrollbarWidth: "none" }}>
          {groups.length === 0 && (
            <Typography sx={{ p: 2, fontSize: 13, color: "#6b7280", fontFamily: FONT }}>This book has no headings.</Typography>
          )}
          {groups.map((group) => (
            <React.Fragment key={group.id}>
              <ListItem button sx={{ pl: 1, py: 0.5, "&:hover": { backgroundColor: "rgba(95,99,104,0.08)" } }}>
                {group.children.length > 0 && (
                  <IconButton size="small" aria-label={expanded[group.id] ? "Collapse" : "Expand"}
                    onClick={(e) => { e.stopPropagation(); setExpanded((p) => ({ ...p, [group.id]: !p[group.id] })); }}
                    sx={{ p: 0.25, mr: 0.5 }}>
                    {expanded[group.id] ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                  </IconButton>
                )}
                <ListItemText primary={group.text} onClick={() => scrollTo(group.id)}
                  sx={{ ml: group.children.length === 0 ? 3.5 : 0, cursor: "pointer",
                    "& .MuiListItemText-primary": { fontSize: group.level === 1 ? "1rem" : "0.9rem",
                      fontWeight: group.level === 1 ? 500 : 400, color: group.level === 1 ? "#333" : "#666" } }} />
              </ListItem>
              {group.children.length > 0 && (
                <Collapse in={!!expanded[group.id]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {group.children.map((child) => (
                      <ListItem key={child.id} button onClick={() => scrollTo(child.id)}
                        sx={{ pl: child.level === 2 ? 5 : 7, py: 0.25, "&:hover": { backgroundColor: "rgba(95,99,104,0.08)" } }}>
                        <ListItemText primary={child.text}
                          sx={{ "& .MuiListItemText-primary": { fontSize: "0.85rem", fontWeight: 400, color: "#666" } }} />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          ))}
        </List>
      </Drawer>
      )}

      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", minWidth: 0, width: tocShown ? `calc(100% - ${TOC_WIDTH}px)` : "100%" }}>
        <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, backgroundColor: "#fff", color: "#5f6368", padding: "8px 16px",
          display: "flex", alignItems: "center", boxShadow: "0 1px 2px 0 rgba(60,64,67,0.3), 0 2px 6px 2px rgba(60,64,67,0.15)",
          zIndex: 1000, height: "56px", boxSizing: "border-box" }}>
          <IconButton onClick={() => navigate("/dashboard/study")} aria-label="Back to Study Anything"
            sx={{ color: "#5f6368", mr: 2, width: 40, height: 40, border: "1px solid #dadce0", borderRadius: "50%",
              backgroundColor: "#f1f3f4", "&:hover": { backgroundColor: "#e8eaed" } }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <IconButton onClick={toggleToc} aria-label={drawerOpen ? "Hide contents" : "Show contents"} title={drawerOpen ? "Hide contents" : "Show contents"}
            sx={{ color: "#5f6368", mr: 2, width: 40, height: 40, border: "1px solid #dadce0", borderRadius: "50%",
              backgroundColor: drawerOpen ? "#e8f1f6" : "#f1f3f4", "&:hover": { backgroundColor: "#e8eaed" } }}>
            {drawerOpen ? <MenuOpenIcon fontSize="small" /> : <MenuIcon fontSize="small" />}
          </IconButton>
          <Box component="h1" sx={{ fontSize: "1.125rem", fontWeight: 400, m: 0, flexGrow: 1, color: "#202124",
            fontFamily: "Google Sans, Roboto, Arial, sans-serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {book.title}
          </Box>
          <Typography sx={{ fontSize: 12, color: "#6b7280", fontFamily: FONT, mr: 1, whiteSpace: "nowrap" }}>
            {book.pages} page{book.pages === 1 ? "" : "s"}
          </Typography>
        </Box>

        <Box ref={paneRef} sx={{ pt: "72px", pb: 6, width: "100%", pl: 3, pr: isMobile ? 2 : 3, position: "relative", overflowY: "auto",
          height: "100vh", maxWidth: "100%",
          "& img": { maxWidth: "100%", height: "auto" },
          "& h1": { color: "#333", marginTop: "0.5em", fontSize: "2rem" },
          "& h2": { color: "#333", marginTop: "1em", fontSize: "1.5rem" },
          "& h3": { color: "#333", marginTop: "1em", fontSize: "1.25rem" },
          "& p, & li": { lineHeight: 1.7, color: "#333", fontSize: "1.02rem" },
          "& code": { backgroundColor: "#f5f5f5", padding: "2px 4px", borderRadius: "4px", fontSize: "0.92em" },
          "& pre": { backgroundColor: "#f6f8fa", padding: "12px 14px", borderRadius: "8px", overflowX: "auto" },
          "& pre code": { backgroundColor: "transparent", padding: 0 },
          "& blockquote": { borderLeft: "3px solid #cbd5e1", margin: "1em 0", padding: "0.25em 1em", color: "#4b5563" },
          "& table": { borderCollapse: "collapse", display: "block", overflowX: "auto", maxWidth: "100%" },
          "& th, & td": { border: "1px solid #e5e7eb", padding: "6px 10px" },
          // A readable line length, centred in whatever room the pane has.
          "& > div": { maxWidth: 1040, mx: "auto" } }}>
          <div ref={contentRef} data-testid="study-book" onMouseUp={onSelect} onKeyUp={onSelect}>
            {rendered}
          </div>
        </Box>
      </Box>

      <ReaderTutorPanel mobile={isMobile} openSignal={quote}>
        <StudyTutor bookId={Number(bookId)} place={place} quote={quote} onClearQuote={clearQuote} onSource={onSource} />
      </ReaderTutorPanel>
    </Box>
  );
}
